import { useEffect, useMemo, useState } from 'react';

import { useUserExperiments } from '~hooks/queries/users/query';
import { useUserId } from '~hooks/useUserId';

import { EXPERIMENT_VARIANTS } from '../constants';
import { UseExperimentResult } from '../types';

const useLocalStorageOverride = (experimentKey: string): string | null => {
  const [override, setOverride] = useState<string | null>(null);

  useEffect(() => {
    // exp_{experimentKey} 형식으로 개별 키 조회 (예: exp_home_account_nudge)
    const value = localStorage.getItem(`exp_${experimentKey}`);
    if (value) setOverride(value);
  }, [experimentKey]);

  return override;
};

export function useExperiment(experimentKey: string): UseExperimentResult {
  const userId = useUserId();
  const { data: userExperiments, isLoading } = useUserExperiments({ userId });
  const localStorageOverride = useLocalStorageOverride(experimentKey);

  const variant = useMemo(() => {
    return userExperiments?.Data?.data?.find((exp) => exp.experimentKey === experimentKey)?.variant;
  }, [userExperiments, experimentKey]);

  const isControl = useMemo(() => {
    if (localStorageOverride) return localStorageOverride === EXPERIMENT_VARIANTS.CONTROL;
    return variant === EXPERIMENT_VARIANTS.CONTROL;
  }, [variant, localStorageOverride]);

  const isTreatment = useMemo(() => {
    if (localStorageOverride) return localStorageOverride === EXPERIMENT_VARIANTS.TREATMENT;
    return variant === EXPERIMENT_VARIANTS.TREATMENT;
  }, [variant, localStorageOverride]);

  const abTestLogValue = useMemo(() => {
    const experiments = userExperiments?.Data?.data;
    if (!experiments) return undefined;
    const obj = experiments.reduce<Record<string, string | undefined>>(
      (acc, { experimentKey, variant }) => ({ ...acc, [experimentKey]: variant }),
      {},
    );
    return JSON.stringify(obj);
  }, [userExperiments]);

  return {
    variant,
    isControl,
    isTreatment,
    isLoading,
    abTestLogValue,
  };
}
